<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { DialogCloseButton, DialogDrawer } from '@ilteducation/components';
import useLocale from '../../../composables/use-locale';

const props = defineProps<{
  modelValue: boolean;
  loading?: boolean;
  initialName?: string;
  initialDescription?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'save', data: { name: string; description?: string }): void;
}>();

const { t } = useLocale();

const name = ref('');
const description = ref('');
const nameField = ref<{ focus(): void } | null>(null);

watch(
  () => props.modelValue,
  async (val) => {
    if (val) {
      name.value = props.initialName ?? '';
      description.value = props.initialDescription ?? '';
      await nextTick();
      nameField.value?.focus();
    } else {
      name.value = '';
      description.value = '';
    }
  },
);

const handleClose = () => {
  if (props.loading) {
    return;
  }
  emit('update:modelValue', false);
};

const handleSave = () => {
  emit('save', { name: name.value, description: description.value || undefined });
};
</script>

<template>
  <DialogDrawer
    :model-value="modelValue"
    :titleText="t('save_dialog.title')"
    cardColor="rgb(219, 219, 219)"
    maxWidth="500"
    @close="handleClose"
  >
    <template #dialogTopbar>
      <DialogCloseButton @click="handleClose" icon="fa-whiteboard fa-times" />
      <v-card-title class="pb-6 text-center position-relative">
        <h2 class="chalkduster-font text-black ma-0">{{ t('save_dialog.title') }}</h2>
      </v-card-title>
    </template>

    <div class="d-flex flex-column ga-4">
      <v-text-field
        ref="nameField"
        v-model="name"
        :label="t('save_dialog.name_label')"
        rounded="default"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
        color="black"
        base-color="black"
        bg-color="rgba(255,255,255,0.5)"
        maxlength="100"
        :rules="[(v) => !!v?.trim() || t('save_dialog.name_required_error')]"
        validate-on="blur"
      />
      <v-textarea
        v-model="description"
        :placeholder="t('save_dialog.description_label')"
        variant="outlined"
        density="comfortable"
        hide-details
        color="black"
        base-color="black"
        bg-color="rgba(255,255,255,0.5)"
        rows="3"
        maxlength="500"
        no-resize
      />
    </div>

    <template #actions>
      <v-btn size="large" color="black" variant="outlined" rounded="default" @click="handleClose">
        {{ t('button_cancel') }}
      </v-btn>
      <v-btn size="large" variant="flat" :disabled="!name.trim()" :loading="loading" @click="handleSave">
        {{ t('button_save') }}
      </v-btn>
    </template>
  </DialogDrawer>
</template>

<style scoped lang="scss"></style>
